import { pickT } from "@repo/shared";
import { Pencil, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DragHandleCell,
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { FlowPageWithComponents } from "@/lib/types/flow";
import { cn } from "@/lib/utils";
import { T } from "./page-section.i18n";
import { PageSectionHeader } from "./page-section-header";
import {
  canAcceptDrop,
  type DragContext,
  type PageSection,
  type SectionKey,
} from "./page-sections";

/** Everything a section needs besides its own `section`; the list passes these through. */
export type PageSectionSharedProps = {
  flowId: string;
  /** The flow's language: the editor shows its UI in it. */
  flowLanguage: string;
  loading: boolean;
  /** The page whose delete-check is in flight (QL-168), for its Delete button's spinner. */
  checkingDeleteId: string | null;
  dragContext: DragContext;
  onAddToSection: (section: PageSection) => void;
  onEdit: (page: FlowPageWithComponents) => void;
  onDelete: (page: FlowPageWithComponents) => void;
  onDragStart: (page: FlowPageWithComponents, sectionKey: SectionKey) => void;
  onDragEnd: () => void;
  /** True once the current drag was dropped into another section (the page moved away). */
  wasMovedAway: () => boolean;
  onReorderWithinSection: (pageIds: string[]) => Promise<void>;
  onMoveToSection: (pageId: string, target: PageSection, targetIndex: number) => Promise<void>;
};

type PageSectionProps = PageSectionSharedProps & { section: PageSection };

const sameIds = (a: FlowPageWithComponents[], b: FlowPageWithComponents[]) =>
  a.length === b.length && a.every((page, index) => page.id === b[index]?.id);

/**
 * One page section: header strip + its pages as a reorderable, click-to-enter table. Drag within
 * the section reorders (one request with the full section); a drop from a compatible section
 * moves the page (one request, the server renumbers both sections). An empty section is its
 * header strip and a muted "No pages yet" line — which is also its drop target.
 *
 * Cross-section drag-and-drop needs the shared drag context, so this keeps its own HTML5 drag
 * handlers instead of the single-list `useDragReorder` hook. The drag state is cleared
 * synchronously at `dragend`, before any request, so a drag that starts while a request is in
 * flight is a fresh drag; the optimistic list is only ever replaced by the latest server list
 * (kept in a ref, so a refetch that lands mid-drag or mid-dialog is never overwritten by a
 * stale closure).
 */
export function PageSectionView({
  flowId,
  section,
  flowLanguage,
  loading,
  checkingDeleteId,
  dragContext,
  onAddToSection,
  onEdit,
  onDelete,
  onDragStart,
  onDragEnd,
  wasMovedAway,
  onReorderWithinSection,
  onMoveToSection,
}: PageSectionProps) {
  const t = pickT(T, flowLanguage);
  const [orderedPages, setOrderedPages] = useState(section.pages);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Mirrors for the async/continuous handlers, which must not read a stale render's values.
  const latestPagesRef = useRef(section.pages);
  const draggedIndexRef = useRef<number | null>(null);

  // Track the server list; adopt it unless a drag is in progress (the drag end re-syncs).
  useEffect(() => {
    latestPagesRef.current = section.pages;
    if (draggedIndexRef.current === null) setOrderedPages(section.pages);
  }, [section.pages]);

  // A cancelled external drag may never fire `dragleave` here; forget its drop index.
  useEffect(() => {
    if (dragContext === null) setDropTargetIndex(null);
  }, [dragContext]);

  const canAcceptCurrentDrag = dragContext
    ? canAcceptDrop(section.key, dragContext.sourceSection)
    : false;
  const isExternalDrag = dragContext != null && dragContext.sourceSection !== section.key;
  const showDropHighlight = isExternalDrag && canAcceptCurrentDrag;
  // A drop is possible here for a drag of this section's own rows or a compatible external one.
  const acceptsDrop = draggedIndexRef.current !== null || (isExternalDrag && canAcceptCurrentDrag);

  const setDragged = (index: number | null) => {
    draggedIndexRef.current = index;
    setDraggedIndex(index);
  };

  const handleDragStartInternal = (
    e: React.DragEvent,
    index: number,
    page: FlowPageWithComponents,
  ) => {
    e.dataTransfer.effectAllowed = "move";
    setDragged(index);
    onDragStart(page, section.key);
  };

  // Calling `preventDefault` is what tells the browser a drop is allowed; without it the
  // pointer shows "no drop" — which is the honest cursor over a section that will refuse.
  const allowDrop = (e: React.DragEvent) => {
    if (!acceptsDrop) {
      e.dataTransfer.dropEffect = "none";
      return false;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    return true;
  };

  const handleDragOverRow = (e: React.DragEvent, index: number) => {
    if (!allowDrop(e)) return;

    if (!isExternalDrag) {
      // Own row being dragged: shuffle the optimistic list. Functional update + ref, because
      // `dragover` fires continuously and two events can land before a re-render.
      const from = draggedIndexRef.current;
      if (from === null || from === index) return;
      setOrderedPages((current) => {
        const next = [...current];
        const [dragged] = next.splice(from, 1);
        next.splice(index, 0, dragged);
        return next;
      });
      setDragged(index);
      return;
    }

    setDropTargetIndex(index);
  };

  const handleDragOverContainer = (e: React.DragEvent) => {
    if (!allowDrop(e)) return;
    // A `dragover` on a row bubbles here in the same dispatch, after the row handler queued its
    // index — the functional update keeps that index and only fills in "append at the end" for
    // a drag over the container's empty area.
    if (isExternalDrag) {
      setDropTargetIndex((current) => current ?? orderedPages.length);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget as Node)) {
      setDropTargetIndex(null);
    }
  };

  // One drop handler, on the container only: a drop on a row bubbles up to it, and the row's
  // `dragover` already keeps `dropTargetIndex` pointing at that row. A second handler on the row
  // would fire the move twice for one drop.
  const handleDrop = (e: React.DragEvent) => {
    if (!(isExternalDrag && canAcceptCurrentDrag && dragContext)) return;
    e.preventDefault();
    const targetIndex = dropTargetIndex ?? orderedPages.length;
    setDropTargetIndex(null);
    void onMoveToSection(dragContext.page.id, section, targetIndex);
  };

  // `dragend` fires on the dragged row, i.e. in the SOURCE section, whatever happened to the
  // drop. Commit a reorder only when the drag stayed in this section (a drop into another
  // section already moved the page and renumbered both sections server-side) and the order
  // actually changed; otherwise snap back to the server list. The drag state is cleared before
  // the request so the next drag is independent of this one.
  const handleDragEnd = () => {
    const wasOwnDrag = draggedIndexRef.current !== null && !isExternalDrag;
    const snapshot = orderedPages;
    setDragged(null);
    setDropTargetIndex(null);
    onDragEnd();

    const latest = latestPagesRef.current;
    const commit = wasOwnDrag && !wasMovedAway() && !sameIds(snapshot, latest);
    if (!commit) {
      setOrderedPages(latest);
      return;
    }
    void onReorderWithinSection(snapshot.map((page) => page.id)).catch(() => {
      // The server refused the new order (e.g. someone else added a page to this section):
      // show what the server holds — the latest list, which a refetch may already have replaced.
      setOrderedPages(latestPagesRef.current);
    });
  };

  const appendMarker = isExternalDrag && dropTargetIndex === orderedPages.length;

  return (
    <div className="space-y-3">
      <PageSectionHeader
        title={section.title}
        meta={section.meta}
        repeats={section.repeats}
        repeatedLabel={t.repeated}
        addLabel={t.addPage}
        onAdd={section.canAdd ? () => onAddToSection(section) : undefined}
      />

      {/* biome-ignore lint/a11y/noStaticElementInteractions: container needs drag handlers for cross-section drops */}
      <div
        ref={containerRef}
        onDragOver={handleDragOverContainer}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {orderedPages.length === 0 ? (
          <p
            className={cn(
              "rounded-md px-3 py-2 text-sm text-muted-foreground",
              showDropHighlight && "ring-2 ring-primary",
            )}
          >
            {t.noPagesYet}
          </p>
        ) : (
          <Table
            variant="interactive"
            containerClassName={showDropHighlight ? "ring-2 ring-primary" : ""}
          >
            <TableHeader>
              <TableRow>
                <TableHead className="w-12" />
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.components}</TableHead>
                <TableActionsHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {orderedPages.map((page, index) => (
                <TableRow
                  key={page.id}
                  draggable
                  onDragStart={(e) => handleDragStartInternal(e, index, page)}
                  onDragOver={(e) => handleDragOverRow(e, index)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    draggedIndex === index && "opacity-50",
                    isExternalDrag && dropTargetIndex === index && "border-t-2 border-t-primary",
                    appendMarker &&
                      index === orderedPages.length - 1 &&
                      "border-b-2 border-b-primary",
                  )}
                >
                  <DragHandleCell />
                  {/* draggable={false}: a link is its own drag source, which would hijack
                      the row's drag-to-reorder; with it off, the drag starts on the row. */}
                  <TableCellLink
                    className="font-medium"
                    to={`/settings/flows/${flowId}/pages/${page.id}`}
                    draggable={false}
                  >
                    {page.name}
                  </TableCellLink>
                  <TableCell className="text-muted-foreground">
                    {page.components.length}{" "}
                    {page.components.length !== 1 ? t.components.toLowerCase() : t.component}
                  </TableCell>
                  <TableActionsCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      tooltip={t.edit}
                      onClick={() => onEdit(page)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      tooltip={t.delete}
                      onClick={() => onDelete(page)}
                      loading={checkingDeleteId === page.id}
                      disabled={loading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableActionsCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
